import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, HelpCircle, Loader2 } from "lucide-react";
import { SafetyQuestion, SafetyQuestionType, QUESTION_TYPE_LABEL, SafetyCategory, SafetyItem } from "./types";

interface Props { templateId: string; locked: boolean; }

const emptyForm = {
  question: "",
  question_type: "single_choice" as SafetyQuestionType,
  category_id: "",
  item_id: "",
  answer_options_text: "",
  correct_answer_text: "",
  explanation: "",
  is_required: true,
  is_scored: true,
  requires_manual_review: false,
  difficulty: 1,
};

export const SafetyQuestionsManager = ({ templateId, locked }: Props) => {
  const [cats, setCats] = useState<SafetyCategory[]>([]);
  const [items, setItems] = useState<SafetyItem[]>([]);
  const [questions, setQuestions] = useState<SafetyQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<SafetyQuestion | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data: cs } = await supabase
      .from("safety_instruction_categories").select("*")
      .eq("template_id", templateId).order("sort_order");
    const catsList = (cs as unknown as SafetyCategory[]) || [];
    setCats(catsList);
    if (catsList.length) {
      const { data: its } = await supabase
        .from("safety_instruction_items").select("*")
        .in("category_id", catsList.map((c) => c.id)).order("sort_order");
      const itemsList = (its as unknown as SafetyItem[]) || [];
      setItems(itemsList);
      const ids = catsList.map((c) => c.id);
      const { data: qs } = await supabase
        .from("safety_instruction_questions").select("*")
        .in("category_id", ids).order("sort_order");
      setQuestions((qs as unknown as SafetyQuestion[]) || []);
    } else {
      setItems([]); setQuestions([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [templateId]);

  const openDialog = (q?: SafetyQuestion) => {
    if (q) {
      setEditing(q);
      const opts = Array.isArray(q.answer_options) ? (q.answer_options as any[]).map((o) => typeof o === "string" ? o : o.label).join("\n") : "";
      const correct = q.correct_answer ? (Array.isArray(q.correct_answer) ? (q.correct_answer as any[]).join("\n") : String(q.correct_answer)) : "";
      setForm({
        question: q.question, question_type: q.question_type,
        category_id: q.category_id || "", item_id: q.item_id || "",
        answer_options_text: opts, correct_answer_text: correct,
        explanation: q.explanation || "", is_required: q.is_required, is_scored: q.is_scored,
        requires_manual_review: q.requires_manual_review, difficulty: q.difficulty,
      });
    } else {
      setEditing(null);
      setForm({ ...emptyForm, category_id: cats[0]?.id || "" });
    }
    setDialog(true);
  };

  const save = async () => {
    if (!form.question.trim() || !form.category_id) { toast.error("Frage und Kategorie sind erforderlich"); return; }
    setSaving(true);
    try {
      let answer_options: any = [];
      let correct_answer: any = null;
      if (["single_choice", "multi_choice"].includes(form.question_type)) {
        answer_options = form.answer_options_text.split("\n").map((s) => s.trim()).filter(Boolean).map((label) => ({ label }));
        const corrects = form.correct_answer_text.split("\n").map((s) => s.trim()).filter(Boolean);
        correct_answer = form.question_type === "single_choice" ? (corrects[0] || null) : corrects;
      } else if (form.question_type === "yes_no") {
        answer_options = [{ label: "Ja" }, { label: "Nein" }];
        correct_answer = form.correct_answer_text.trim() || null;
      }

      const payload: any = {
        question: form.question.trim(), question_type: form.question_type,
        category_id: form.category_id, item_id: form.item_id || null,
        answer_options, correct_answer, explanation: form.explanation || null,
        is_required: form.is_required, is_scored: form.is_scored,
        requires_manual_review: form.requires_manual_review, difficulty: form.difficulty,
      };
      if (editing) {
        const { error } = await supabase.from("safety_instruction_questions").update(payload).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("safety_instruction_questions").insert(payload);
        if (error) throw error;
      }
      toast.success("Gespeichert"); setDialog(false); load();
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  };

  const del = async (id: string) => {
    if (!confirm("Frage löschen?")) return;
    const { error } = await supabase.from("safety_instruction_questions").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Gelöscht"); load(); }
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (cats.length === 0) return <p className="text-center py-8 text-muted-foreground text-sm">Bitte zuerst Kategorien anlegen.</p>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Fragenkatalog</CardTitle>
          <Button size="sm" onClick={() => openDialog()} disabled={locked} className="gap-2">
            <Plus className="h-4 w-4" /> Neue Frage
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {questions.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground text-sm">Noch keine Fragen.</p>
        ) : (
          <div className="space-y-2">
            {cats.map((c) => {
              const qs = questions.filter((q) => q.category_id === c.id);
              if (qs.length === 0) return null;
              return (
                <div key={c.id}>
                  <h4 className="font-medium text-sm mb-2 mt-3">{c.title}</h4>
                  <div className="space-y-2">
                    {qs.map((q) => (
                      <div key={q.id} className="border rounded p-3 flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">{q.question}</span>
                            <Badge variant="outline" className="text-xs">{QUESTION_TYPE_LABEL[q.question_type]}</Badge>
                            {q.is_required && <Badge variant="secondary" className="text-xs">Pflicht</Badge>}
                            {q.requires_manual_review && <Badge variant="secondary" className="text-xs">Manuelle Prüfung</Badge>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" disabled={locked} onClick={() => openDialog(q)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" disabled={locked} onClick={() => del(q.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Frage bearbeiten" : "Neue Frage"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Frage *</Label><Textarea rows={2} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Fragetyp</Label>
                <Select value={form.question_type} onValueChange={(v) => setForm({ ...form, question_type: v as SafetyQuestionType })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(QUESTION_TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kategorie *</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v, item_id: "" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Unterweisungspunkt (optional)</Label>
              <Select value={form.item_id || "none"} onValueChange={(v) => setForm({ ...form, item_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Kategorie-übergreifend" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">– kein spezifischer Punkt –</SelectItem>
                  {items.filter((it) => it.category_id === form.category_id).map((it) => <SelectItem key={it.id} value={it.id}>{it.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {["single_choice", "multi_choice"].includes(form.question_type) && (
              <>
                <div><Label>Antwortmöglichkeiten (eine pro Zeile)</Label><Textarea rows={4} value={form.answer_options_text} onChange={(e) => setForm({ ...form, answer_options_text: e.target.value })} /></div>
                <div><Label>Richtige Antwort(en) (eine pro Zeile, exakt wie oben)</Label><Textarea rows={2} value={form.correct_answer_text} onChange={(e) => setForm({ ...form, correct_answer_text: e.target.value })} /></div>
              </>
            )}
            {form.question_type === "yes_no" && (
              <div><Label>Richtige Antwort (Ja / Nein)</Label><Input value={form.correct_answer_text} onChange={(e) => setForm({ ...form, correct_answer_text: e.target.value })} /></div>
            )}
            <div><Label>Erklärung bei falscher Antwort</Label><Textarea rows={2} value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center justify-between"><Label>Pflicht</Label><Switch checked={form.is_required} onCheckedChange={(c) => setForm({ ...form, is_required: c })} /></div>
              <div className="flex items-center justify-between"><Label>Wertung</Label><Switch checked={form.is_scored} onCheckedChange={(c) => setForm({ ...form, is_scored: c })} /></div>
              <div className="flex items-center justify-between"><Label>Manuell</Label><Switch checked={form.requires_manual_review} onCheckedChange={(c) => setForm({ ...form, requires_manual_review: c })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving}>{saving ? "..." : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
